import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TopNavigation from '../../components/base/TopNavigation';
import BottomNavigation from '../../components/base/BottomNavigation';

interface ListItem {
  id: number;
  text: string;
  completed: boolean;
  addedBy: string;
}

export default function ListsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('shopping');
  const [newItem, setNewItem] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  const [shoppingList, setShoppingList] = useState<ListItem[]>([
    { id: 1, text: 'خضار وفواكه', completed: false, addedBy: 'فاطمة' },
    { id: 2, text: 'لحم للعشاء', completed: true, addedBy: 'أحمد' },
    { id: 3, text: 'خبز طازج', completed: false, addedBy: 'سارة' },
    { id: 4, text: 'حليب ولبن', completed: false, addedBy: 'محمد' }
  ]);

  const [houseTasks, setHouseTasks] = useState<ListItem[]>([
    { id: 1, text: 'تنظيف المطبخ', completed: true, addedBy: 'فاطمة' },
    { id: 2, text: 'غسيل الملابس', completed: false, addedBy: 'سارة' },
    { id: 3, text: 'ترتيب غرفة المعيشة', completed: false, addedBy: 'أحمد' },
    { id: 4, text: 'سقي النباتات', completed: true, addedBy: 'محمد' }
  ]);

  const toggleItem = (id: number, listType: string) => {
    if (listType === 'shopping') {
      setShoppingList(prev => prev.map(item => 
        item.id === id ? { ...item, completed: !item.completed } : item
      ));
    } else {
      setHouseTasks(prev => prev.map(item => 
        item.id === id ? { ...item, completed: !item.completed } : item
      ));
    }
  };

  const addItem = () => {
    if (!newItem.trim()) return;
    
    const newListItem: ListItem = {
      id: Date.now(),
      text: newItem,
      completed: false,
      addedBy: 'أنت'
    };

    if (activeTab === 'shopping') {
      setShoppingList(prev => [...prev, newListItem]);
    } else {
      setHouseTasks(prev => [...prev, newListItem]);
    }

    setNewItem('');
    setShowAddForm(false);
  };

  const currentList = activeTab === 'shopping' ? shoppingList : houseTasks;
  const completedCount = currentList.filter(item => item.completed).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <TopNavigation 
        title={t('lists')}
        rightAction={
          <button 
            onClick={() => setShowAddForm(true)}
            className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center"
          >
            <i className="ri-add-line text-lg"></i>
          </button>
        }
      />
      
      <div className="pt-16 pb-20 px-4">
        {/* Tab Switcher */}
        <div className="bg-white rounded-2xl p-1 mb-4 shadow-sm">
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => setActiveTab('shopping')}
              className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'shopping'
                  ? 'bg-[#5B9BD5] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-shopping-cart-line mr-2"></i>
              {t('shoppingList')}
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'tasks'
                  ? 'bg-[#5B9BD5] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-home-4-line mr-2"></i>
              {t('houseTasks')}
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-800">التقدم</h3>
            <span className="text-sm text-gray-600">
              {completedCount} من {currentList.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-[#81C784] h-2 rounded-full transition-all duration-300"
              style={{ width: `${(completedCount / currentList.length) * 100}%` }}
            />
          </div>
        </div>

        {/* List Items */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="space-y-3">
            {currentList.map((item) => (
              <div 
                key={item.id} 
                className={`flex items-center p-3 rounded-xl border transition-all ${
                  item.completed 
                    ? 'bg-[#81C784]/10 border-[#81C784]/20' 
                    : 'bg-gray-50 border-gray-100'
                }`}
              >
                <button
                  onClick={() => toggleItem(item.id, activeTab)}
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-3 transition-all ${
                    item.completed
                      ? 'bg-[#81C784] border-[#81C784]'
                      : 'border-gray-300 hover:border-[#5B9BD5]'
                  }`}
                >
                  {item.completed && (
                    <i className="ri-check-line text-white text-sm"></i>
                  )}
                </button>
                <div className="flex-1">
                  <p className={`font-medium ${
                    item.completed 
                      ? 'text-gray-500 line-through' 
                      : 'text-gray-800'
                  }`}>
                    {item.text}
                  </p>
                  <p className="text-xs text-gray-500">أضافه {item.addedBy}</p>
                </div>
                <button className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600">
                  <i className="ri-more-2-line"></i>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Add Item Form */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/50 flex items-end z-50">
            <div className="bg-white rounded-t-3xl p-6 w-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">{t('addItem')}</h3>
                <button 
                  onClick={() => setShowAddForm(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  placeholder="اكتب العنصر الجديد..."
                  className="w-full p-4 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#5B9BD5]"
                  autoFocus
                />
                <div className="flex gap-3">
                  <button
                    onClick={addItem}
                    className="flex-1 bg-[#5B9BD5] text-white py-3 rounded-xl font-medium"
                  >
                    {t('add')}
                  </button>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="flex-1 bg-gray-100 text-gray-600 py-3 rounded-xl font-medium"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
}